// src/services/BookingService.js
import { ref, push, set, get, serverTimestamp, update } from 'firebase/database';
import { database } from '../firebase';

/**
 * Service for handling booking-related operations
 */
class BookingService {
  /**
   * Save a new booking to the user's account in Firebase
   * 
   * @param {string} userId - The user's Firebase UID
   * @param {object} bookingData - The booking details to save
   * @param {object} paymentIntent - The Stripe payment intent object
   * @returns {Promise<string>} - The ID of the newly created booking
   */
  static async saveBooking(userId, bookingData, paymentIntent) {
    try {
      // Reference to the user's bookings collection
      const bookingsRef = ref(database, `users/${userId}/bookings`);
      
      // Create a new booking entry with a unique ID
      const newBookingRef = push(bookingsRef);
      
      // Format the booking data for storage
      const formattedBooking = this.formatBookingData(bookingData, paymentIntent);
      
      // Save the booking data to Firebase
      await set(newBookingRef, {
        ...formattedBooking,
        id: newBookingRef.key,
        createdAt: serverTimestamp(),
      });
      
      // Return the booking ID
      return newBookingRef.key;
    } catch (error) {
      console.error('Error saving booking:', error);
      throw new Error('Failed to save booking');
    }
  }
  
  /**
   * Save a booking for a guest user (not authenticated)
   * 
   * @param {object} bookingData - The booking details to save
   * @param {object} paymentIntent - The Stripe payment intent object
   * @param {string} guestEmail - The guest user's email address
   * @param {string} guestPhone - The guest user's phone number (optional)
   * @returns {Promise<{id: string, bookingData: object}>} - The ID and data of the newly created booking
   */
  static async saveGuestBooking(bookingData, paymentIntent, guestEmail, guestPhone) {
    try {
      // Reference to the guest bookings collection
      const guestBookingsRef = ref(database, 'guestBookings');
      
      // Create a new booking entry with a unique ID
      const newBookingRef = push(guestBookingsRef);
      const bookingId = newBookingRef.key;
      
      // Format the booking data for storage
      const formattedBooking = this.formatBookingData(bookingData, paymentIntent);
      
      // Add guest-specific information
      const guestBooking = {
        ...formattedBooking,
        id: bookingId,
        isGuest: true,
        guestEmail: guestEmail,
        guestPhone: guestPhone || '',
        createdAt: serverTimestamp(),
      };
      
      // Save the booking data to Firebase
      await set(newBookingRef, guestBooking);
      
      // Create a lookup record by email for easy reference
      if (guestEmail) {
        const safeEmail = guestEmail.replace(/[.#$[\]]/g, '_');
        await set(ref(database, `guestLookup/byEmail/${safeEmail}/${bookingId}`), {
          bookingId: bookingId,
          date: formattedBooking.schedule.date,
          service: formattedBooking.service.title
        });
      }
      
      // Save confirmation info to localStorage for retrieval in confirmation page
      localStorage.setItem('lastBookingId', bookingId);
      localStorage.setItem('lastBookingDetails', JSON.stringify({
        id: bookingId,
        service: formattedBooking.service.title,
        date: formattedBooking.schedule.date,
        time: formattedBooking.schedule.time,
        email: guestEmail
      }));
      
      // Return the booking ID and data
      return {
        id: bookingId,
        bookingData: guestBooking
      };
    } catch (error) {
      console.error('Error saving guest booking:', error);
      throw new Error('Failed to save guest booking');
    }
  }
  
  /**
   * Format the booking data to match the structure expected by AppointmentsTab
   * 
   * @param {object} bookingData - The raw booking data
   * @param {object} paymentIntent - The Stripe payment intent object
   * @returns {object} - The formatted booking data
   */
  static formatBookingData(bookingData, paymentIntent) {
    const { serviceDetails, schedulingDetails, vehicleDetails } = bookingData;
    
    // Make sure the date is stored as a string for Firebase
    const scheduleDateString = schedulingDetails.date instanceof Date 
      ? schedulingDetails.date.toISOString() 
      : schedulingDetails.date;
    
    // Calculate final payment details
    const subtotal = serviceDetails.adjustedPrice || serviceDetails.price;
    const tax = subtotal * 0.0825; // 8.25% tax rate (should be configurable in a real app)
    const total = subtotal + tax;
    
    // Format the booking data to match the structure expected by AppointmentsTab
    return {
      service: {
        id: serviceDetails.id,
        title: serviceDetails.title,
        price: serviceDetails.price,
        category: serviceDetails.category
      },
      schedule: {
        date: scheduleDateString,
        time: schedulingDetails.time
      },
      vehicle: {
        ...vehicleDetails.vehicle,
        location: vehicleDetails.location,
        instructions: vehicleDetails.instructions
      },
      payment: {
        subtotal: subtotal,
        tax: tax,
        total: total,
        paymentIntentId: paymentIntent?.id || null,
        paymentMethodId: paymentIntent?.payment_method || null,
        last4: paymentIntent?.payment_method_details?.card?.last4 || null
      },
      status: 'confirmed', // Initial status when booking is created
      notes: '',
      paymentIntentId: paymentIntent?.id || null, // Add at top level for easier searching
      // Include the full booking data for reference
      _originalBookingData: {
        serviceDetails,
        schedulingDetails,
        vehicleDetails
      }
    };
  }
  
  /**
   * Update the status of an existing booking
   * 
   * @param {string} userId - The user's Firebase UID, or 'guest' for guest bookings
   * @param {string} bookingId - The ID of the booking to update
   * @param {string} status - The new status (confirmed, pending, completed, cancelled)
   * @returns {Promise<void>}
   */
  static async updateBookingStatus(userId, bookingId, status) {
    try {
      let bookingRef;
      
      // Determine if this is a guest booking or user booking
      if (userId === 'guest') {
        bookingRef = ref(database, `guestBookings/${bookingId}/status`);
      } else {
        bookingRef = ref(database, `users/${userId}/bookings/${bookingId}/status`);
      }
      
      await set(bookingRef, status);
    } catch (error) {
      console.error('Error updating booking status:', error);
      throw new Error('Failed to update booking status');
    }
  }
  
  /**
   * Reschedule an existing booking to a new date and time
   *
   * @param {string} userId - The user's Firebase UID, or 'guest' for guest bookings
   * @param {string} bookingId - The ID of the booking to reschedule
   * @param {string} newDate - The new date (ISO string)
   * @param {string} newTime - The new time slot string (e.g. '10:00 AM')
   * @returns {Promise<void>}
   */
  static async rescheduleBooking(userId, bookingId, newDate, newTime) {
    try {
      const basePath = userId === 'guest'
        ? `guestBookings/${bookingId}`
        : `users/${userId}/bookings/${bookingId}`;

      const updates = {};
      updates[`${basePath}/schedule/date`] = newDate;
      updates[`${basePath}/schedule/time`] = newTime;
      updates[`${basePath}/rescheduledAt`] = new Date().toISOString();

      await update(ref(database), updates);
    } catch (error) {
      console.error('Error rescheduling booking:', error);
      throw new Error('Failed to reschedule booking');
    }
  }

  /**
   * Add a note to an existing booking
   * 
   * @param {string} userId - The user's Firebase UID, or 'guest' for guest bookings
   * @param {string} bookingId - The ID of the booking to update
   * @param {string} note - The note to add
   * @returns {Promise<void>}
   */
  static async addBookingNote(userId, bookingId, note) {
    try {
      let bookingRef;
      
      // Determine if this is a guest booking or user booking
      if (userId === 'guest') {
        bookingRef = ref(database, `guestBookings/${bookingId}/notes`);
      } else {
        bookingRef = ref(database, `users/${userId}/bookings/${bookingId}/notes`);
      }
      
      await set(bookingRef, note);
    } catch (error) {
      console.error('Error adding booking note:', error);
      throw new Error('Failed to add booking note');
    }
  }
  
  /**
   * Retrieve a booking by ID (works for both user and guest bookings)
   * 
   * @param {string} bookingId - The ID of the booking to retrieve
   * @param {string} userId - Optional user ID if known
   * @returns {Promise<object|null>} - The booking data or null if not found
   */
  static async getBookingById(bookingId, userId = null) {
    try {
      // If user ID is provided, check user's bookings first
      if (userId) {
        const userBookingRef = ref(database, `users/${userId}/bookings/${bookingId}`);
        const userSnapshot = await get(userBookingRef);
        
        if (userSnapshot.exists()) {
          return userSnapshot.val();
        }
      }
      
      // Check guest bookings if not found in user bookings
      const guestBookingRef = ref(database, `guestBookings/${bookingId}`);
      const guestSnapshot = await get(guestBookingRef);
      
      if (guestSnapshot.exists()) {
        return guestSnapshot.val();
      }
      
      // Not found in either location
      return null;
    } catch (error) {
      console.error('Error retrieving booking:', error);
      throw new Error('Failed to retrieve booking');
    }
  }
  
  /**
   * Find bookings by guest email address
   * 
   * @param {string} email - The guest email to search for
   * @returns {Promise<Array>} - Array of booking objects
   */
  static async findBookingsByGuestEmail(email) {
    try {
      const safeEmail = email.replace(/[.#$[\]]/g, '_');
      const lookupRef = ref(database, `guestLookup/byEmail/${safeEmail}`);
      const snapshot = await get(lookupRef);
      
      if (!snapshot.exists()) {
        return [];
      }
      
      const lookups = snapshot.val();
      const bookings = [];
      
      // Fetch each booking by ID
      for (const [lookupId, lookupData] of Object.entries(lookups)) {
        const bookingRef = ref(database, `guestBookings/${lookupData.bookingId}`);
        const bookingSnapshot = await get(bookingRef);
        
        if (bookingSnapshot.exists()) {
          bookings.push(bookingSnapshot.val());
        }
      }
      
      return bookings;
    } catch (error) {
      console.error('Error finding bookings by email:', error);
      throw new Error('Failed to find bookings');
    }
  }
}

export default BookingService;